import {
  AddClientRequest,
  AddClientResponse,
  ClientData,
  ClientStatus,
  DisplayResultMessage,
  EditClientRequest,
  GetWhitelistIpsRequest,
  OpenConfirmModal,
  UsageType,
} from "@shared/types";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  PRIMARY_EXCHANGES,
  QUICK_CONNECT_EXCHANGES,
} from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import {
  getCreateOrEditAccountErrorMessage,
  getEditAccountSuccessMessage,
} from "@shared/features/onboarding/utils";
import { CREATE_ACCOUNT_ERROR_DEFAULT_TITLE } from "@shared/features/onboarding/constants";
import { UseQuotaModal } from "@shared/features/settings/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { useClientsData, useUser } from "@shared/utils/hooks/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";

interface useClientFormProps {
  account?: ClientData;
  primaryExchange?: string;
  onSubmitSuccess?: () => void;
  closeDrawer?: () => void;
  displayResultMessage: DisplayResultMessage;
  useWhitelistIPs: (
    exchange?: string,
    args?: GetWhitelistIpsRequest
  ) => {
    whitelistIPs: ReactNode;
    checkAllowSave: () => boolean;
  };
  selectedExchange?: string;
  onConnectToExchangeClicked?: () => void;
  getErrorNotification?: (description: string, title?: string) => void;
  useQuotaModal: UseQuotaModal;
  openConfirmModal: OpenConfirmModal<any>;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  isOnboarding?: boolean;
}

const useSharedClientForm = ({
  account,
  primaryExchange,
  onSubmitSuccess,
  closeDrawer,
  displayResultMessage,
  useWhitelistIPs,
  selectedExchange,
  onConnectToExchangeClicked,
  getErrorNotification,
  useQuotaModal,
  openConfirmModal,
  getSuccessMessage,
  isOnboarding,
}: useClientFormProps) => {
  const { getActiveAccountsWithSameApis } = useClientsData() || {};
  const { isExternalConnectAccount, token } =
    useContext(ExternalConnectAccountContext) || {};
  const {
    setTab,
    exchangesData,
    exchangesLoading,
    isDemoWorkspace,
    exchangeToClientName,
    handleOnSubmit,
    accountName,
    accountNameError,
    selectedSubExchanges,
    getAccountSubExchange,
    addClientSucceeded,
    setAddClientSucceeded,
  } = useContext(AccountFormContext)!;
  const isEdit = account !== undefined;
  const [showPassphrase, setShowPassphrase] = useState(false);
  const { user, isFetching: userIsFetching } = useUser();

  const getWhitelistIpsArgs = useMemo(() => {
    if (!account?.client_id && !token) return undefined;
    let request: GetWhitelistIpsRequest = {};
    if (account?.client_id) request.client_id = account.client_id;
    if (token) request.authToken = token;
    return request;
  }, [account?.client_id, token]);
  const { isFetching: isFetchingWhitelistIPs } =
    sharedLib.sharedApi.configuration.useGetWhitelistIPsQuery(
      getWhitelistIpsArgs
    );
  const [checkApiKeyExistence, checkApiKeyExistenceStatus] =
    sharedLib.sharedApi.configuration.useLazyCheckApiKeyExistenceQuery();
  const [addClient, addClientStatus] =
    sharedLib.sharedApi.configuration.useAddClientMutation();
  const [createAccountFromInvite, createAccountFromInviteStatus] =
    sharedLib.sharedApi.configuration.useCreateAccountFromInviteMutation();
  const [editClient, editClientStatus] =
    sharedLib.sharedApi.configuration.useEditClientMutation();

  const { whitelistIPs, checkAllowSave } = useWhitelistIPs(
    primaryExchange,
    getWhitelistIpsArgs
  );
  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuotaCheck,
  } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });

  const isQuickConnectExchange =
    !!primaryExchange && QUICK_CONNECT_EXCHANGES.has(primaryExchange);
  const isHyperLiquid = primaryExchange === PRIMARY_EXCHANGES.HYPERLIQUID;

  useEffect(() => {
    if (selectedExchange) {
      handleCheckShowPassphrase(selectedExchange);
    }
  }, [selectedExchange]);

  const isLoadingSaveClient =
    addClientStatus.isLoading ||
    createAccountFromInviteStatus.isLoading ||
    editClientStatus.isLoading ||
    checkApiKeyExistenceStatus.isFetching ||
    userIsFetching;

  const handleCheckShowPassphrase = (exchange: string) => {
    const selectedExchange = exchangesData?.find(
      (exchangeInfo) => exchangeInfo.nested_exchange === exchange
    );
    setShowPassphrase(!!selectedExchange && !!selectedExchange.api_passphrase);
  };

  useEffect(() => {
    if (addClientSucceeded && !!user && !userIsFetching) {
      onSubmitSuccess?.();
      closeDrawer?.();
    }
  }, [addClientSucceeded, user, userIsFetching]);

  const handleSaveClientData = (
    formValues: AddClientRequest | (EditClientRequest & { exchange: string })
  ) => {
    const errorMessage = getCreateOrEditAccountErrorMessage(
      isEdit ? "edit" : "create"
    );

    if (!isEdit) {
      let promise: Promise<void>;
      if (isExternalConnectAccount) {
        const addClientRequest = {
          ...formValues,
          exchanges: selectedSubExchanges?.map((exchange) => exchange.exchange),
          authToken: token!,
        };
        promise = createAccountFromInvite(addClientRequest).unwrap();
      } else {
        const addClientRequest = {
          ...formValues,
          exchange_to_client_name: exchangeToClientName,
          is_demo_query: isDemoWorkspace,
        };
        promise = addClient(addClientRequest).unwrap();
      }
      onConnectToExchangeClicked?.();
      displayResultMessage(
        promise,
        undefined,
        errorMessage,
        undefined,
        undefined,
        [400],
        true,
        true
      )
        .then((response) => {
          getSuccessMessage?.(response);
          if (isOnboarding && !isExternalConnectAccount) {
            setAddClientSucceeded(true);
          } else {
            closeDrawer?.();
            onSubmitSuccess?.();
          }
        })
        .catch((response) => {
          if (response.data?.code === 400)
            getErrorNotification?.(
              response.data.message,
              response.data.title ?? CREATE_ACCOUNT_ERROR_DEFAULT_TITLE
            );
        });
    } else {
      let { exchange, ...editClientRequest } = formValues;
      editClientRequest = {
        ...editClientRequest,
        client_id: account?.client_id!,
        client_name: accountName,
      };
      const successMessage = getEditAccountSuccessMessage(exchange);
      displayResultMessage(
        editClient(editClientRequest as EditClientRequest).unwrap(),
        successMessage,
        errorMessage,
        10,
        () => {
          onSubmitSuccess?.();
          closeDrawer?.();
        }
      );
    }
  };

  const onFinish = async (
    formValues: AddClientRequest | (EditClientRequest & { exchange: string })
  ) => {
    if (!checkAllowSave() && !isDemoWorkspace) return;

    try {
      let shouldCheckQuota: boolean = false;
      if (!isExternalConnectAccount) {
        const accountsWithSameApis = account
          ? getActiveAccountsWithSameApis?.(account.client_id)
          : undefined;
        const hasAccountsWithSameApisDifferentUuid = accountsWithSameApis?.some(
          (otherAccount) =>
            account?.client_unique_identifier !==
            otherAccount.client_unique_identifier
        );
        if (
          formValues.api_key &&
          (!isEdit ||
            account?.client_status !== ClientStatus.VALID ||
            hasAccountsWithSameApisDifferentUuid)
        ) {
          shouldCheckQuota = !(await checkApiKeyExistence({
            api_key: formValues.api_key,
          }).unwrap());
        }
      }
      let passedQuota: boolean | undefined;
      if (shouldCheckQuota) {
        passedQuota = await handleTriggerClick();
      }
      if (!passedQuota) {
        handleOnSubmit(
          primaryExchange,
          () => handleSaveClientData(formValues),
          openConfirmModal
        );
      }
    } catch {}
  };

  const handleExchangeChange = (exchange: string) => {
    handleCheckShowPassphrase(exchange);
  };

  useEffect(() => {
    if (primaryExchange) {
      handleExchangeChange(primaryExchange);
    }
  }, [primaryExchange]);

  const apiFieldNames: ApiFieldName[] = ["api_key", "api_secret"];
  if (showPassphrase) {
    apiFieldNames.push("api_passphrase");
  }

  const validateApiInfo = (
    formValues: any,
    fieldName: ApiFieldName,
    fieldLabel: string
  ) => {
    const fieldValue = formValues[fieldName];
    if (!account && !fieldValue) {
      return `Please provide an ${fieldLabel}.`;
    }

    const restApiFieldNames = apiFieldNames.filter(
      (name) => name !== fieldName
    );
    if (
      account &&
      !fieldValue &&
      restApiFieldNames.some((name) => formValues[name])
    ) {
      return "Please do not provide partial API information.";
    }
  };

  const mainButtonData = {
    text: isEdit
      ? "Update"
      : isDemoWorkspace
      ? "Create Demo Account"
      : `Connect to ${primaryExchange}`,
    loading: isLoadingSaveClient || isLoadingQuotaCheck,
    disabled: accountNameError || (!isEdit && !selectedSubExchanges?.length),
  };

  const secondaryButtonData = {
    text:
      isQuickConnectExchange &&
      !isEdit &&
      !isDemoWorkspace &&
      !isExternalConnectAccount
        ? `Use ${primaryExchange} quick connect`
        : undefined,
    onClick: () => setTab(ConnectExchangeTabs.QUICK_CONNECT),
  };

  const showChangeAccountTypeMessage = !isEdit;

  const accountSubExchange = useMemo(
    () => getAccountSubExchange(account),
    [account]
  );

  return {
    isFetchingWhitelistIPs,
    isQuickConnectExchange,
    primaryExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    handleExchangeChange,
    validateApiInfo,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    showChangeAccountTypeMessage,
    exchangesLoading,
    accountSubExchange,
    isHyperLiquid,
  };
};

export default useSharedClientForm;

export type ApiFieldName = "api_key" | "api_secret" | "api_passphrase";
